import { FloorPlan3DMaterialSlot } from "../products/product3DTypes";

export type BuiltInFurnitureMaterialFamily =
  | "wood"
  | "metal"
  | "fabric"
  | "leather"
  | "plastic"
  | "stone"
  | "glass"
  | "concrete"
  | "rattan";

export type SurfaceMaterialKind = "opaque" | "metal" | "glass";

export type BuiltInFurnitureMaterial = {
  /**
   * Surface UUID (source "surface") or stable procedural preset key
   * (source "material").
   */
  id: string;
  /** How this id should be persisted on materialAssignments / surfaceRef. */
  source: "surface" | "material";
  /** Display name. */
  label: string;
  /** Optional i18n key for older UI paths. */
  labelKey?: string;
  color: number;
  roughness: number;
  metalness: number;
  families: BuiltInFurnitureMaterialFamily[];
  suggestedSlots: FloorPlan3DMaterialSlot[];
  categoryName?: string;
  materialKind: SurfaceMaterialKind;
  /** Catalog albedo URL (Surface.images[0]). */
  textureUrl?: string;
  /** Raw Surface.images for bake / Ready checks. */
  surfaceImages?: Array<string | null> | null;
  /** Default UV repeat when applying textureUrl / catalog maps. */
  repeatScale?: number;
  /** Cloth sheen (MeshPhysicalMaterial). Soft fabrics / velvet. */
  sheen?: number;
  sheenRoughness?: number;
  sheenColor?: number;
  /** When set, render with MeshPhysicalMaterial transmission. */
  transmission?: number;
  ior?: number;
  thickness?: number;
};

/** Soft-fabric PBR: mild pile sheen + finer UV so albedo grain reads as weave. */
const FABRIC_PBR = {
  color: 0xc9c0b0,
  roughness: 0.72,
  metalness: 0,
  sheen: 1,
  sheenRoughness: 0.55,
  sheenColor: 0xffffff,
  repeatScale: 4,
} as const;

/** Stronger grazing sheen for velvet / velour / chenille names. */
const VELVET_PBR = {
  color: 0xc9c0b0,
  roughness: 0.62,
  metalness: 0,
  sheen: 1,
  sheenRoughness: 0.38,
  sheenColor: 0xffffff,
  repeatScale: 5,
} as const;

/** Wallpaper / mural — one tile every 4 m (tiles-per-metre = 0.25). */
const WALLPAPER_PBR = {
  color: 0xe8e0d5,
  roughness: 0.78,
  metalness: 0,
  repeatScale: 0.25,
} as const;

function isVelvetLikeName(name: string | undefined): boolean {
  const key = (name ?? "").trim().toLowerCase();
  return (
    key.includes("velvet") ||
    key.includes("velour") ||
    key.includes("chenille") ||
    key.includes("corduroy")
  );
}

function isWallpaperLike(
  categoryName: string | undefined,
  materialName?: string,
): boolean {
  const category = (categoryName ?? "").trim().toLowerCase();
  const name = (materialName ?? "").trim().toLowerCase();
  return (
    category.includes("wallpaper") ||
    category.includes("mural") ||
    name.includes("wallpaper") ||
    name.includes("mural")
  );
}

const ALL_HARD_SLOTS: FloorPlan3DMaterialSlot[] = [
  "furnitureWood",
  "doorPanel",
  "doorFrame",
  "stair",
  "floor",
  "wall",
];

const METAL_SLOTS: FloorPlan3DMaterialSlot[] = [
  "furnitureMetal",
  "doorHardware",
  "doorHandle",
  "furnitureScreen",
];

const SOFT_SLOTS: FloorPlan3DMaterialSlot[] = ["furnitureFabric"];

/** Glass is valid on openings plus hard surfaces (stairs/shapes/tables). */
const GLASS_SLOTS: FloorPlan3DMaterialSlot[] = [
  "furnitureGlass",
  "windowGlass",
  "stair",
  "wall",
];

const STONE_SLOTS: FloorPlan3DMaterialSlot[] = [
  "furnitureWood",
  "doorPanel",
  "stair",
  "floor",
];

const GLASS_DEFAULTS = {
  transmission: 1,
  ior: 1.5,
  thickness: 0.05,
} as const;

const CATEGORY_PBR: Record<
  string,
  {
    color: number;
    roughness: number;
    metalness: number;
    sheen?: number;
    sheenRoughness?: number;
    sheenColor?: number;
    repeatScale?: number;
  }
> = {
  // Hard materials omit repeatScale so CatalogStandardMaterial defaults to 1
  // (matches staging). Admin preview defaults of 2 made oak look over-tiled.
  wood: { color: 0xc89b62, roughness: 0.65, metalness: 0 },
  stone: { color: 0xeeeeee, roughness: 0.45, metalness: 0 },
  fabric: { ...FABRIC_PBR },
  leather: { color: 0x3b2118, roughness: 0.5, metalness: 0 },
  metal: { color: 0x8a8a8a, roughness: 0.35, metalness: 1 },
  glass: { color: 0xffffff, roughness: 0.05, metalness: 0 },
  concrete: { color: 0x9a9a96, roughness: 0.85, metalness: 0 },
  rattan: { color: 0xc4a574, roughness: 0.8, metalness: 0 },
  wallpaper: { ...WALLPAPER_PBR },
  mural: { ...WALLPAPER_PBR },
};

/**
 * Map SurfaceCategory name → family + suggested slots for the Material picker.
 */
export function categoryToFamilyAndSlots(categoryName: string | undefined): {
  family: BuiltInFurnitureMaterialFamily;
  suggestedSlots: FloorPlan3DMaterialSlot[];
} {
  const key = (categoryName ?? "").trim().toLowerCase();
  switch (key) {
    case "metal":
      return { family: "metal", suggestedSlots: METAL_SLOTS };
    case "fabric":
      return { family: "fabric", suggestedSlots: SOFT_SLOTS };
    case "leather":
      return { family: "leather", suggestedSlots: SOFT_SLOTS };
    case "glass":
      return { family: "glass", suggestedSlots: GLASS_SLOTS };
    case "stone":
      return { family: "stone", suggestedSlots: STONE_SLOTS };
    case "concrete":
      return { family: "concrete", suggestedSlots: STONE_SLOTS };
    case "rattan":
      return { family: "rattan", suggestedSlots: ALL_HARD_SLOTS };
    case "wallpaper":
    case "mural":
      return { family: "wood", suggestedSlots: ALL_HARD_SLOTS };
    case "wood":
    default:
      return { family: "wood", suggestedSlots: ALL_HARD_SLOTS };
  }
}

export function getCategoryPbrDefaults(
  categoryName: string | undefined,
  materialName?: string,
): {
  color: number;
  roughness: number;
  metalness: number;
  sheen?: number;
  sheenRoughness?: number;
  sheenColor?: number;
  repeatScale?: number;
} {
  const key = (categoryName ?? "").trim().toLowerCase();
  if (key === "fabric" && isVelvetLikeName(materialName)) {
    return { ...VELVET_PBR };
  }
  if (isWallpaperLike(categoryName, materialName)) {
    return { ...WALLPAPER_PBR };
  }
  return CATEGORY_PBR[key] ?? { color: 0xc0c0c0, roughness: 0.7, metalness: 0 };
}

/**
 * Procedural metal/glass presets that do not require a baked Surface texture.
 * Stable ids are persisted with source "material".
 */
export const PROCEDURAL_BUILTIN_MATERIALS: BuiltInFurnitureMaterial[] = [
  {
    id: "furniture-steel",
    source: "material",
    label: "Steel",
    labelKey: "materials.steel",
    color: 0x777777,
    roughness: 0.3,
    metalness: 0.95,
    families: ["metal"],
    suggestedSlots: METAL_SLOTS,
    categoryName: "Metal",
    materialKind: "metal",
  },
  {
    id: "furniture-brushed-metal",
    source: "material",
    label: "Brushed metal",
    labelKey: "materials.brushedMetal",
    color: 0x666666,
    roughness: 0.5,
    metalness: 1,
    families: ["metal"],
    suggestedSlots: METAL_SLOTS,
    categoryName: "Metal",
    materialKind: "metal",
  },
  {
    id: "furniture-brass",
    source: "material",
    label: "Brass",
    labelKey: "materials.brass",
    color: 0xd9c083,
    roughness: 0.35,
    metalness: 1,
    families: ["metal"],
    suggestedSlots: METAL_SLOTS,
    categoryName: "Metal",
    materialKind: "metal",
  },
  {
    id: "furniture-chrome",
    source: "material",
    label: "Chrome",
    labelKey: "materials.chrome",
    color: 0xc0c0c0,
    roughness: 0.15,
    metalness: 1,
    families: ["metal"],
    suggestedSlots: METAL_SLOTS,
    categoryName: "Metal",
    materialKind: "metal",
  },
  {
    id: "furniture-black-metal",
    source: "material",
    label: "Black metal",
    labelKey: "materials.blackMetal",
    color: 0x1a1a1a,
    roughness: 0.35,
    metalness: 0.95,
    families: ["metal"],
    suggestedSlots: METAL_SLOTS,
    categoryName: "Metal",
    materialKind: "metal",
  },
  {
    id: "furniture-glass",
    source: "material",
    label: "Glass",
    labelKey: "materials.glass",
    color: 0xffffff,
    roughness: 0.05,
    metalness: 0,
    families: ["glass"],
    suggestedSlots: GLASS_SLOTS,
    categoryName: "Glass",
    materialKind: "glass",
    ...GLASS_DEFAULTS,
  },
  {
    id: "furniture-smoked-glass",
    source: "material",
    label: "Smoked glass",
    labelKey: "materials.smokedGlass",
    color: 0x4a4a4a,
    roughness: 0.08,
    metalness: 0,
    families: ["glass"],
    suggestedSlots: GLASS_SLOTS,
    categoryName: "Glass",
    materialKind: "glass",
    ...GLASS_DEFAULTS,
  },
  {
    id: "furniture-frosted-glass",
    source: "material",
    label: "Frosted glass",
    labelKey: "materials.frostedGlass",
    color: 0xe8e8e8,
    roughness: 0.35,
    metalness: 0,
    families: ["glass"],
    suggestedSlots: GLASS_SLOTS,
    categoryName: "Glass",
    materialKind: "glass",
    ...GLASS_DEFAULTS,
  },
];

/** Legacy hex-catalog / older preset ids that map onto the procedural pack. */
const BUILT_IN_MATERIAL_ALIASES: Record<string, string> = {
  "metal-brass": "furniture-brass",
  glass: "furniture-glass",
  "furniture-brushed-stainless": "furniture-brushed-metal",
};

export type SurfaceCatalogApiRow = {
  id: string;
  name?: string | null;
  images?: Array<string | null> | null;
  /** Optional per-Surface PBR; null/undefined = category defaults. */
  roughness?: number | null;
  sheen?: number | null;
  sheenRoughness?: number | null;
  repeatScale?: number | null;
  categories?: Array<{
    id?: string | null;
    name?: string | null;
    order?: number | null;
  } | null> | null;
};

let surfaceMaterialsById = new Map<string, BuiltInFurnitureMaterial>();
let surfaceMaterialsList: BuiltInFurnitureMaterial[] = [];

/**
 * Replace the in-memory Surface-backed catalog from the GraphQL response.
 * Caller should pass only baked Surfaces.
 */
export function setSurfaceCatalogMaterialsFromApi(
  rows: SurfaceCatalogApiRow[],
): void {
  surfaceMaterialsList = rows.map(surfaceRowToMaterial);
  surfaceMaterialsById = new Map(
    surfaceMaterialsList.map((material) => [material.id, material]),
  );
}

/** @deprecated Prefer setSurfaceCatalogMaterialsFromApi */
export const setBuiltInFurnitureMaterialsFromApi =
  setSurfaceCatalogMaterialsFromApi;

export function clearBuiltInFurnitureMaterialsForTests(): void {
  surfaceMaterialsById = new Map();
  surfaceMaterialsList = [];
}

function surfaceRowToMaterial(row: SurfaceCatalogApiRow): BuiltInFurnitureMaterial {
  const categoryName = row.categories?.[0]?.name ?? undefined;
  const label = row.name?.trim() || "Untitled";
  const { family, suggestedSlots } = categoryToFamilyAndSlots(categoryName);
  const pbr = getCategoryPbrDefaults(categoryName, label);
  const kind: SurfaceMaterialKind =
    family === "glass" ? "glass" : family === "metal" ? "metal" : "opaque";
  const albedo = row.images?.[0] ?? undefined;
  // Prefer optional Surface overrides; null/undefined keeps category defaults.
  const roughness = row.roughness ?? pbr.roughness;
  const sheen = row.sheen ?? pbr.sheen;
  const sheenRoughness = row.sheenRoughness ?? pbr.sheenRoughness;
  const repeatScale = row.repeatScale ?? pbr.repeatScale;
  return {
    id: row.id,
    source: "surface",
    label,
    // Keep the category tint for loading/failed-albedo fallbacks and solid
    // swatches. CatalogStandardMaterial forces white once the map is bound.
    color: pbr.color,
    roughness,
    metalness: pbr.metalness,
    families: [family],
    suggestedSlots,
    categoryName,
    materialKind: kind,
    textureUrl: albedo,
    surfaceImages: row.images ?? null,
    ...(repeatScale !== undefined ? { repeatScale } : {}),
    ...(sheen !== undefined ? { sheen } : {}),
    ...(sheenRoughness !== undefined ? { sheenRoughness } : {}),
    ...(pbr.sheenColor !== undefined ? { sheenColor: pbr.sheenColor } : {}),
    ...(kind === "glass" ? GLASS_DEFAULTS : {}),
  };
}

function mergedMaterials(): BuiltInFurnitureMaterial[] {
  return [...PROCEDURAL_BUILTIN_MATERIALS, ...surfaceMaterialsList];
}

function materialsByIdMap(): Map<string, BuiltInFurnitureMaterial> {
  const map = new Map<string, BuiltInFurnitureMaterial>();
  for (const material of mergedMaterials()) {
    map.set(material.id, material);
  }
  return map;
}

export function resolveBuiltInFurnitureMaterialId(
  materialId: string | undefined,
): string | undefined {
  if (!materialId) return undefined;
  const alias = BUILT_IN_MATERIAL_ALIASES[materialId];
  if (alias) return alias;
  if (materialsByIdMap().has(materialId)) return materialId;
  return undefined;
}

export function isBuiltInFurnitureMaterialId(
  materialId: string | undefined,
): boolean {
  return resolveBuiltInFurnitureMaterialId(materialId) !== undefined;
}

export function getBuiltInFurnitureMaterial(
  materialId: string | undefined,
): BuiltInFurnitureMaterial | undefined {
  const resolvedId = resolveBuiltInFurnitureMaterialId(materialId);
  return resolvedId ? materialsByIdMap().get(resolvedId) : undefined;
}

export function listBuiltInFurnitureMaterials(): BuiltInFurnitureMaterial[] {
  return mergedMaterials();
}

export function listBuiltInFurnitureMaterialsForSlot(
  slot: FloorPlan3DMaterialSlot,
): BuiltInFurnitureMaterial[] {
  const all = mergedMaterials();
  const suggested = all.filter((material) =>
    material.suggestedSlots.includes(slot),
  );
  if (suggested.length > 0) return suggested;
  return all;
}

export function isGlassBuiltInFurnitureMaterial(
  material: BuiltInFurnitureMaterial | undefined,
): boolean {
  return Boolean(
    material &&
      (material.materialKind === "glass" || material.transmission !== undefined),
  );
}

/**
 * Persist a picker selection with the correct source discriminator.
 *
 * Lean refs: store id + PBR overrides only. Do not embed `imageUrl` — resolve
 * albedo at render via catalog mappings / built-in `textureUrl` (legacy saved
 * URLs still render as a fallback).
 */
export function builtInFurnitureMaterialToSurfaceRef(
  material: BuiltInFurnitureMaterial,
): {
  source: "surface" | "material";
  id: string;
  color: number;
  roughness: number;
  metalness: number;
  repeatScale?: number;
  sheen?: number;
  sheenRoughness?: number;
  sheenColor?: number;
} {
  const hasImage = Boolean(material.textureUrl);
  return {
    source: material.source,
    id: material.id,
    color: hasImage ? 0xffffff : material.color,
    roughness: material.roughness,
    metalness: material.metalness,
    ...(material.repeatScale !== undefined
      ? { repeatScale: material.repeatScale }
      : {}),
    ...(material.sheen !== undefined ? { sheen: material.sheen } : {}),
    ...(material.sheenRoughness !== undefined
      ? { sheenRoughness: material.sheenRoughness }
      : {}),
    ...(material.sheenColor !== undefined
      ? { sheenColor: material.sheenColor }
      : {}),
  };
}
